
import * as React from "react"

const MOBILE_BREAKPOINT = 768
const DEBOUNCE_DELAY = 100

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)
  const timeoutRef = React.useRef<NodeJS.Timeout>()
  const frameRef = React.useRef<number>()

  const checkIsMobile = React.useCallback(() => {
    // Clear any pending frame
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current)
    }

    // Use requestAnimationFrame to ensure DOM is stable
    frameRef.current = requestAnimationFrame(() => {
      const width = window.innerWidth
      const newIsMobile = width < MOBILE_BREAKPOINT
      
      // console.log('🔍 useIsMobile: Stable width check:', {width,breakpoint: MOBILE_BREAKPOINT,isMobile: newIsMobile,timestamp: Date.now()})
      setIsMobile(newIsMobile)
    })
  }, [])

  React.useEffect(() => {
    // console.log('🔍 useIsMobile: Hook initialized');
    
    // Initial check with delay to ensure layout is stable
    const initialTimeout = setTimeout(() => {
      checkIsMobile()
    }, 50)

    const handleResize = () => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Debounce resize events
      timeoutRef.current = setTimeout(() => {
        // console.log('🔍 useIsMobile: Resize triggered, checking mobile state')
        checkIsMobile()
      }, DEBOUNCE_DELAY)
    }

    // Use both resize and orientationchange for better mobile support
    window.addEventListener("resize", handleResize)
    window.addEventListener("orientationchange", handleResize)
    
    return () => {
      // console.log('🔍 useIsMobile: Cleanup')
      
      // Clear timeouts and animation frames
      clearTimeout(initialTimeout)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
      
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("orientationchange", handleResize)
    }
  }, [checkIsMobile])

  const finalValue = !!isMobile
  // console.log('🔍 useIsMobile: Returning value:', finalValue, 'at', Date.now())
  
  return finalValue
}
